import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import WreathCard from '../../components/wreath/WreathCard';
import Button from '../../components/common/Button';
import ErrorState from '../../components/common/ErrorState';
import { CardGridSkeleton } from '../../components/common/Skeleton';
import { fetchWreaths } from '../../utils/api';
import { useCart } from '../../context/CartContext';

const CATEGORIES = ['전체', '프리미엄', '스탠다드', '이코노미'];

const PRICE_RANGES = [
  { value: 'all', label: '전체 가격' },
  { value: '0-80000', label: '8만원 이하', min: 0, max: 80000 },
  { value: '80000-120000', label: '8~12만원', min: 80000, max: 120000 },
  { value: '120000-160000', label: '12~16만원', min: 120000, max: 160000 },
  { value: '160000-', label: '16만원 이상', min: 160000, max: Infinity },
];

const SORT_OPTIONS = [
  { value: 'default', label: '기본순' },
  { value: 'price-asc', label: '가격 낮은순' },
  { value: 'price-desc', label: '가격 높은순' },
  { value: 'name', label: '이름순' },
];

const WreathListPage = () => {
  const navigate = useNavigate();
  const { isInCart } = useCart();

  const [wreaths, setWreaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [hideSoldOut, setHideSoldOut] = useState(false);

  const loadWreaths = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchWreaths();
      if (!data || data.length === 0) {
        // fetch 유틸이 실패 시 빈 배열을 반환하므로 구분이 어려움 → 빈 배열도 일단 표시
        setWreaths(data || []);
      } else {
        setWreaths(data);
      }
    } catch (e) {
      console.error('화환 데이터 로드 실패:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWreaths();
  }, [loadWreaths]);

  // 카테고리별 개수
  const categoryCounts = useMemo(() => {
    const counts = { 전체: wreaths.length };
    wreaths.forEach((w) => {
      counts[w.category] = (counts[w.category] || 0) + 1;
    });
    return counts;
  }, [wreaths]);

  // 필터 + 정렬
  const filteredWreaths = useMemo(() => {
    let result = [...wreaths];

    if (selectedCategory !== '전체') {
      result = result.filter((w) => w.category === selectedCategory);
    }

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      result = result.filter(
        (w) =>
          w.name.toLowerCase().includes(term) ||
          (w.features || []).some((f) => f.toLowerCase().includes(term)) ||
          (w.description || '').toLowerCase().includes(term)
      );
    }

    if (priceRange !== 'all') {
      const range = PRICE_RANGES.find((r) => r.value === priceRange);
      if (range) {
        result = result.filter((w) => w.price >= range.min && w.price < range.max);
      }
    }

    if (hideSoldOut) {
      result = result.filter((w) => w.inStock);
    }

    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        break;
      default:
        // 품절 상품은 항상 뒤로
        result.sort((a, b) => Number(b.inStock) - Number(a.inStock));
        break;
    }

    return result;
  }, [wreaths, selectedCategory, searchTerm, priceRange, sortBy, hideSoldOut]);

  const handleWreathClick = (wreathId) => {
    navigate(`/wreaths/${wreathId}`);
  };

  const resetFilters = () => {
    setSelectedCategory('전체');
    setSearchTerm('');
    setPriceRange('all');
    setSortBy('default');
    setHideSoldOut(false);
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* 페이지 헤더 */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-primary mb-2">화환 상품</h1>
          <p className="text-accent-600">고인을 추모하는 마음을 담은 화환을 선택하세요</p>
        </div>

        {/* 검색 */}
        <div className="mb-4">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-400"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="상품명, 꽃 종류로 검색"
              aria-label="화환 검색"
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-accent-300 bg-white text-primary placeholder-accent-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* 카테고리 필터 (tablist) */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="카테고리 필터">
            {CATEGORIES.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'primary' : 'outline'}
                size="md"
                onClick={() => setSelectedCategory(category)}
                className="flex-shrink-0"
              >
                {category}
                {categoryCounts[category] != null && (
                  <span className="ml-1.5 text-xs opacity-80">({categoryCounts[category] || 0})</span>
                )}
              </Button>
            ))}
          </div>
        </div>

        {/* 정렬 / 가격대 / 품절 숨기기 */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-accent-700">
            <span>정렬</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="정렬 기준"
              className="rounded-lg border border-accent-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm text-accent-700">
            <span>가격대</span>
            <select
              value={priceRange}
              onChange={(e) => setPriceRange(e.target.value)}
              aria-label="가격대 필터"
              className="rounded-lg border border-accent-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {PRICE_RANGES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm text-accent-700 cursor-pointer">
            <input
              type="checkbox"
              checked={hideSoldOut}
              onChange={(e) => setHideSoldOut(e.target.checked)}
              className="w-4 h-4 text-primary rounded"
            />
            품절 상품 숨기기
          </label>
        </div>

        {/* 상품 개수 표시 */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-accent-600" aria-live="polite">
            총 <span className="font-bold text-primary">{filteredWreaths.length}</span>개의 상품
          </p>
        </div>

        {/* 상태별 렌더링 */}
        {loading ? (
          <CardGridSkeleton count={8} columns={4} />
        ) : error ? (
          <ErrorState
            title="화환 목록을 불러오지 못했습니다"
            message="네트워크 상태를 확인한 뒤 다시 시도해주세요."
            onRetry={loadWreaths}
          />
        ) : filteredWreaths.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <p className="text-lg text-accent-600 mb-2">조건에 맞는 상품이 없습니다</p>
              <Button variant="outline" onClick={resetFilters}>
                필터 초기화
              </Button>
            </div>
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 list-none p-0 m-0">
            {filteredWreaths.map((wreath) => (
              <li key={wreath.id}>
                <WreathCard
                  wreath={wreath}
                  isInCart={isInCart(wreath.id)}
                  onClick={() => handleWreathClick(wreath.id)}
                />
              </li>
            ))}
          </ul>
        )}

        {/* 하단 정보 */}
        {!loading && !error && filteredWreaths.length > 0 && (
          <div className="mt-8 p-4 bg-accent-50 rounded-lg border border-accent-200">
            <h3 className="font-bold text-primary mb-2">화환 배송 안내</h3>
            <ul className="text-sm text-accent-700 space-y-1">
              <li>• 주문 후 2-3시간 내 배송이 가능합니다</li>
              <li>• 장례식장 주소로 직접 배송됩니다</li>
              <li>• 배송 전 고객님께 연락 드립니다</li>
              <li>• 문의사항은 고객센터로 연락주세요</li>
            </ul>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default WreathListPage;
